import React, { useEffect, useState, useMemo } from "react";
import { getSchedule, generateSchedule, getUsers } from "./api";
import {
    BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, Cell, PieChart, Pie
} from 'recharts';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';

const styles = {
    container: {
        maxWidth: "1200px",
        margin: "0 auto",
        padding: "32px 24px",
    },
    header: {
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        marginBottom: "24px",
    },
    title: {
        fontSize: "22px",
        fontWeight: 700,
        color: "var(--text-main)",
        margin: 0,
        display: "flex",
        alignItems: "center",
        gap: "10px",
    },
    badge: {
        fontSize: "12px",
        fontWeight: 600,
        padding: "4px 12px",
        borderRadius: "20px",
        backgroundColor: "var(--work-bg)",
        color: "var(--work-text)",
        border: "1px solid var(--work-border)",
    },
    controls: {
        display: "flex",
        gap: "12px",
        marginBottom: "24px",
        flexWrap: "wrap",
        alignItems: "flex-end",
        backgroundColor: "var(--bg-card)",
        padding: "20px",
        borderRadius: "var(--border-radius-base)",
        border: "1px solid var(--border-color)",
        boxShadow: "var(--shadow-sm)",
    },
    formGroup: {
        display: "flex",
        flexDirection: "column",
        gap: "6px",
    },
    label: {
        fontSize: "11px",
        fontWeight: 600,
        color: "var(--text-muted)",
        textTransform: "uppercase",
        letterSpacing: "0.6px",
    },
    input: {
        padding: "10px 14px",
        borderRadius: "10px",
        border: "1px solid var(--border-color)",
        backgroundColor: "var(--bg-main)",
        color: "var(--text-main)",
        fontSize: "14px",
        fontWeight: "500",
        outline: "none",
        minWidth: "160px",
        transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
        cursor: "pointer",
        fontFamily: "inherit",
    },
    stats: {
        display: "grid",
        gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
        gap: "16px",
        marginBottom: "24px",
    },
    analyticsRow: {
        display: "grid",
        gridTemplateColumns: "1fr 1fr",
        gap: "20px",
        marginBottom: "24px",
    },
    chartCard: {
        padding: "20px",
        borderRadius: "var(--border-radius-base)",
        backgroundColor: "var(--bg-card)",
        border: "1px solid var(--border-color)",
        boxShadow: "var(--shadow-sm)",
        height: "350px",
    },
    statCard: {
        padding: "20px",
        borderRadius: "var(--border-radius-base)",
        backgroundColor: "var(--bg-card)",
        border: "1px solid var(--border-color)",
        boxShadow: "var(--shadow-sm)",
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
    },
    statValue: {
        fontSize: "26px",
        fontWeight: 700,
        marginBottom: "6px",
    },
    statLabel: {
        fontSize: "13px",
        color: "var(--text-muted)",
        fontWeight: 500,
    },
    tableWrapper: {
        overflowX: "auto",
        borderRadius: "var(--border-radius-base)",
        border: "1px solid var(--border-color)",
        backgroundColor: "var(--bg-card)",
        boxShadow: "var(--shadow-sm)",
    },
    table: {
        width: "100%",
        borderCollapse: "collapse",
        fontSize: "13px",
    },
    th: {
        padding: "16px",
        textAlign: "left",
        backgroundColor: "var(--bg-header)",
        color: "var(--text-muted)",
        fontWeight: 600,
        fontSize: "11px",
        textTransform: "uppercase",
        borderBottom: "1px solid var(--border-color)",
    },
    td: {
        padding: "12px 16px",
        borderBottom: "1px solid var(--border-color)",
        verticalAlign: "middle",
        color: "var(--text-main)",
    },
    statusWorkPlan: {
        display: "inline-block",
        padding: "4px 12px",
        borderRadius: "6px",
        backgroundColor: "var(--work-bg)",
        color: "var(--work-text)",
        fontWeight: 600,
        fontSize: "11px",
        border: "1px solid var(--work-border)",
    },
    statusRestPlan: {
        display: "inline-block",
        padding: "4px 12px",
        borderRadius: "6px",
        backgroundColor: "var(--danger-bg)",
        color: "var(--danger-text)",
        fontWeight: 600,
        fontSize: "11px",
        border: "1px solid var(--danger-border)",
    },
    statusManagerWork: {
        display: "inline-block",
        padding: "4px 12px",
        borderRadius: "6px",
        backgroundColor: "var(--success-bg)",
        color: "var(--success-text)",
        fontWeight: 600,
        fontSize: "11px",
        border: "1px solid var(--success-border)",
    },
    error: {
        padding: "16px",
        borderRadius: "8px",
        backgroundColor: "var(--danger-bg)",
        color: "var(--danger-text)",
        border: "1px solid var(--danger-border)",
        marginBottom: "20px",
    },
};

const AZ_MONTHS_FULL = [
    "Yanvar", "Fevral", "Mart", "Aprel", "May", "İyun",
    "İyul", "Avqust", "Sentyabr", "Oktyabr", "Noyabr", "Dekabr"
];

const AZ_DAYS_SHORT = ["B.", "B.e.", "Ç.ş.", "Ç.", "C.a.", "C.", "Ş."];

function PremiumDatePicker({ value, onChange, label }) {
    const [isOpen, setIsOpen] = useState(false);
    const [viewDate, setViewDate] = useState(value ? new Date(value) : new Date());

    // Prevent invalid dates
    const safeDate = useMemo(() => {
        const d = new Date(value);
        return isNaN(d.getTime()) ? new Date() : d;
    }, [value]);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (!event.target.closest(".datepicker-container")) {
                setIsOpen(false);
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    const daysInMonth = (year, month) => new Date(year, month + 1, 0).getDate();
    const firstDayOfMonth = (year, month) => new Date(year, month, 1).getDay();

    const handleMonthChange = (dir) => {
        const newDate = new Date(viewDate);
        newDate.setMonth(viewDate.getMonth() + dir);
        setViewDate(newDate);
    };

    const handleYearChange = (dir) => {
        const newDate = new Date(viewDate);
        newDate.setFullYear(viewDate.getFullYear() + dir);
        setViewDate(newDate);
    };

    const handleSelect = (day) => {
        const selected = new Date(viewDate.getFullYear(), viewDate.getMonth(), day);
        const y = selected.getFullYear();
        const m = String(selected.getMonth() + 1).padStart(2, '0');
        const d = String(selected.getDate()).padStart(2, '0');
        onChange(`${y}-${m}-${d}`);
        setIsOpen(false);
    };

    const calendarDays = useMemo(() => {
        const year = viewDate.getFullYear();
        const month = viewDate.getMonth();
        const totalDays = daysInMonth(year, month);
        const firstDay = firstDayOfMonth(year, month);
        const days = [];

        // Padding for previous month
        for (let i = 0; i < firstDay; i++) {
            days.push({ day: null, current: false });
        }

        // Current month days
        for (let i = 1; i <= totalDays; i++) {
            const isToday = new Date().toDateString() === new Date(year, month, i).toDateString();
            const isSelected = safeDate.toDateString() === new Date(year, month, i).toDateString();
            days.push({ day: i, current: true, isToday, isSelected });
        }

        return days;
    }, [viewDate, safeDate]);

    const formattedValue = useMemo(() => {
        if (!value) return "Tarix seçin";
        const d = new Date(value);
        return `${d.getDate()} ${AZ_MONTHS_FULL[d.getMonth()].toLowerCase()} ${d.getFullYear()}`;
    }, [value]);

    return (
        <div className="datepicker-container" style={{ position: 'relative' }}>
            <div
                className={`custom-select-trigger ${isOpen ? 'active' : ''}`}
                onClick={() => setIsOpen(!isOpen)}
                style={{
                    padding: '10px 16px',
                    borderRadius: '12px',
                    border: '1px solid var(--border-color)',
                    backgroundColor: 'var(--bg-card)',
                    color: 'var(--text-main)',
                    cursor: 'pointer',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    fontSize: '14px',
                    fontWeight: '500',
                    minWidth: '200px',
                    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                    boxShadow: isOpen ? '0 0 0 3px rgba(37,99,235,0.1)' : '0 2px 5px rgba(0,0,0,0.02)'
                }}
            >
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                    <span style={{ fontSize: '18px', opacity: 0.7 }}>📅</span>
                    <span>{formattedValue}</span>
                </div>
                <span style={{ fontSize: '10px', transition: 'transform 0.3s ease', transform: isOpen ? 'rotate(180deg)' : 'none', opacity: 0.5 }}>▼</span>
            </div>

            {isOpen && (
                <div className="custom-select-dropdown animate-scale-in" style={{
                    position: 'absolute',
                    top: 'calc(100% + 8px)',
                    left: 0,
                    backgroundColor: 'var(--bg-card)',
                    border: '1px solid var(--border-color)',
                    borderRadius: '16px',
                    boxShadow: '0 15px 40px rgba(0,0,0,0.2)',
                    zIndex: 2000,
                    width: '300px',
                    padding: '16px',
                    backdropFilter: 'blur(12px)',
                    userSelect: 'none'
                }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
                        <button className="btn btn-icon" onClick={() => handleMonthChange(-1)} style={{ padding: '4px 8px' }}>⟨</button>
                        <div style={{ textAlign: 'center' }}>
                            <div style={{ fontWeight: '800', fontSize: '15px', color: 'var(--text-main)' }}>{AZ_MONTHS_FULL[viewDate.getMonth()]}</div>
                            <div style={{ fontSize: '12px', color: 'var(--text-muted)', fontWeight: '600' }}>{viewDate.getFullYear()}</div>
                        </div>
                        <button className="btn btn-icon" onClick={() => handleMonthChange(1)} style={{ padding: '4px 8px' }}>⟩</button>
                    </div>

                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: '4px', textAlign: 'center', marginBottom: '8px' }}>
                        {AZ_DAYS_SHORT.map(d => (
                            <div key={d} style={{ fontSize: '11px', fontWeight: '700', color: 'var(--text-muted)', paddingBottom: '4px' }}>{d}</div>
                        ))}
                    </div>

                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: '4px' }}>
                        {calendarDays.map((d, i) => (
                            <div
                                key={i}
                                onClick={() => d.day && handleSelect(d.day)}
                                style={{
                                    height: '34px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    borderRadius: '8px',
                                    fontSize: '13px',
                                    cursor: d.day ? 'pointer' : 'default',
                                    transition: 'all 0.2s ease',
                                    backgroundColor: d.isSelected ? 'var(--primary-color)' : (d.isToday ? 'var(--inactive-btn-bg)' : 'transparent'),
                                    color: d.isSelected ? 'white' : (d.day ? 'var(--text-main)' : 'transparent'),
                                    fontWeight: d.isSelected || d.isToday ? '700' : '400',
                                    border: d.isToday && !d.isSelected ? '1px solid var(--primary-color)' : 'none',
                                    opacity: d.day ? 1 : 0
                                }}
                                className={d.day ? "calendar-day-hover" : ""}
                            >
                                {d.day}
                            </div>
                        ))}
                    </div>

                    <div style={{ marginTop: '16px', paddingTop: '12px', borderTop: '1px solid var(--border-color)', display: 'flex', justifyContent: 'space-between' }}>
                        <button
                            className="btn"
                            style={{ fontSize: '11px', padding: '4px 10px', border: 'none', color: 'var(--primary-color)' }}
                            onClick={() => {
                                const today = new Date();
                                const y = today.getFullYear();
                                const m = String(today.getMonth() + 1).padStart(2, '0');
                                const d = String(today.getDate()).padStart(2, '0');
                                onChange(`${y}-${m}-${d}`);
                                setViewDate(today);
                                setIsOpen(false);
                            }}
                        >Bugün</button>
                        <div style={{ display: 'flex', gap: '4px' }}>
                            <button className="btn btn-icon" onClick={() => handleYearChange(-1)} style={{ fontSize: '10px', padding: '2px 6px' }}>«</button>
                            <button className="btn btn-icon" onClick={() => handleYearChange(1)} style={{ fontSize: '10px', padding: '2px 6px' }}>»</button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

function SearchableSelect({ options, value, onChange, placeholder }) {
    const [isOpen, setIsOpen] = useState(false);
    const [searchTerm, setSearchTerm] = useState("");
    const [highlightedIndex, setHighlightedIndex] = useState(-1);

    const filteredOptions = useMemo(() => {
        return options.filter(opt =>
            opt.name.toLowerCase().includes(searchTerm.toLowerCase())
        );
    }, [options, searchTerm]);

    const selectedOption = useMemo(() =>
        options.find(opt => opt.id === value),
        [options, value]);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (!event.target.closest(".custom-select-container")) {
                setIsOpen(false);
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    const handleSelect = (opt) => {
        onChange(opt.id, opt.name);
        setSearchTerm("");
        setIsOpen(false);
    };

    return (
        <div className="custom-select-container" style={{ position: 'relative', width: '260px' }}>
            <div
                className={`custom-select-trigger ${isOpen ? 'active' : ''}`}
                onClick={() => setIsOpen(!isOpen)}
                style={{
                    padding: '10px 16px',
                    borderRadius: '12px',
                    border: '1px solid var(--border-color)',
                    backgroundColor: 'var(--bg-card)',
                    color: 'var(--text-main)',
                    cursor: 'pointer',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    fontSize: '14px',
                    fontWeight: '500',
                    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                    boxShadow: isOpen ? '0 0 0 3px rgba(37,99,235,0.1)' : '0 2px 5px rgba(0,0,0,0.02)'
                }}
            >
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', overflow: 'hidden' }}>
                    <span style={{ fontSize: '16px', opacity: 0.7 }}>👤</span>
                    <span style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        {selectedOption ? selectedOption.name : placeholder}
                    </span>
                </div>
                <span style={{ fontSize: '10px', transition: 'transform 0.3s ease', transform: isOpen ? 'rotate(180deg)' : 'none', opacity: 0.5 }}>▼</span>
            </div>

            {isOpen && (
                <div className="custom-select-dropdown animate-fade-in" style={{
                    position: 'absolute',
                    top: 'calc(100% + 8px)',
                    left: 0,
                    right: 0,
                    backgroundColor: 'var(--bg-card)',
                    border: '1px solid var(--border-color)',
                    borderRadius: '16px',
                    boxShadow: '0 15px 35px rgba(0,0,0,0.15)',
                    zIndex: 2000,
                    maxHeight: '320px',
                    display: 'flex',
                    flexDirection: 'column',
                    overflow: 'hidden',
                    backdropFilter: 'blur(10px)'
                }}>
                    <div style={{ padding: '12px', backgroundColor: 'var(--bg-header)', borderBottom: '1px solid var(--border-color)' }}>
                        <input
                            autoFocus
                            type="text"
                            placeholder="Axtarış..."
                            value={searchTerm}
                            onClick={(e) => e.stopPropagation()}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            style={{
                                width: '100%',
                                padding: '10px 14px',
                                borderRadius: '10px',
                                border: '1px solid var(--border-color)',
                                backgroundColor: 'var(--bg-main)',
                                color: 'var(--text-main)',
                                outline: 'none',
                                fontSize: '13px',
                                transition: 'all 0.2s ease'
                            }}
                        />
                    </div>
                    <div style={{ padding: '6px', overflowY: 'auto', flex: 1 }} className="custom-scrollbar">
                        {filteredOptions.length > 0 ? (
                            filteredOptions.map((opt, idx) => (
                                <div
                                    key={opt.id}
                                    className={`custom-select-option ${value === opt.id ? 'selected' : ''}`}
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        handleSelect(opt);
                                    }}
                                    onMouseEnter={() => setHighlightedIndex(idx)}
                                    style={{
                                        padding: '10px 14px',
                                        cursor: 'pointer',
                                        borderRadius: '10px',
                                        fontSize: '13px',
                                        marginBottom: '2px',
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '10px',
                                        transition: 'all 0.2s ease',
                                        backgroundColor: value === opt.id ? 'var(--primary-color)' : (highlightedIndex === idx ? 'var(--inactive-btn-bg)' : 'transparent'),
                                        color: value === opt.id ? 'white' : 'var(--text-main)',
                                        fontWeight: value === opt.id ? '700' : (highlightedIndex === idx ? '600' : '400')
                                    }}
                                >
                                    <div style={{
                                        width: '28px', height: '28px', borderRadius: '50%',
                                        backgroundColor: value === opt.id ? 'rgba(255,255,255,0.2)' : 'var(--inactive-btn-bg)',
                                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                                        fontSize: '10px', fontWeight: '800'
                                    }}>
                                        {opt.name.charAt(0)}
                                    </div>
                                    <span>{opt.name}</span>
                                    {value === opt.id && <span style={{ marginLeft: 'auto', fontSize: '14px' }}>✓</span>}
                                </div>
                            ))
                        ) : (
                            <div style={{ padding: '30px', textAlign: 'center', color: 'var(--text-muted)', fontSize: '13px' }}>
                                <div style={{ fontSize: '24px', marginBottom: '8px' }}>🔍</div>
                                Heç bir işçi tapılmadı
                            </div>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
}


export default function Calendar() {
    const [data, setData] = useState([]);
    const [contractId, setContractId] = useState("");
    const [users, setUsers] = useState([]);
    const [searchQuery, setSearchQuery] = useState("");

    const [startDate, setStartDate] = useState("2026-01-01");
    const [endDate, setEndDate] = useState("2026-12-31");

    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");
    const [generating, setGenerating] = useState(false);

    const fetchUsers = async () => {
        try {
            const res = await getUsers();
            setUsers(res.data);
            if (res.data.length > 0) {
                setContractId(res.data[0].id);
                setSearchQuery(res.data[0].name);
            }
        } catch (e) {
            console.error("Users load error", e);
        }
    };

    const fetchSchedule = async () => {
        if (!contractId) return;
        setLoading(true);
        setError("");
        try {
            const res = await getSchedule(contractId);
            setData(res.data);
        } catch (e) {
            setError(e.response?.status === 404 ? "Cədvəl tapılmadı." : e.message);
            setData([]);
        } finally {
            setLoading(false);
        }
    };

    const handleGenerate = async () => {
        setGenerating(true);
        setError("");
        try {
            await generateSchedule(contractId, startDate, endDate);
            await fetchSchedule();
        } catch (e) {
            setError(e.response?.data?.detail || e.message);
        } finally {
            setGenerating(false);
        }
    };

    useEffect(() => { fetchUsers(); }, []);
    useEffect(() => { if (contractId) fetchSchedule(); }, [contractId]);

    const filteredUsers = useMemo(() => {
        if (!searchQuery) return users;
        return users.filter(u => u.name.toLowerCase().includes(searchQuery.toLowerCase()));
    }, [users, searchQuery]);

    const stats = useMemo(() => {
        if (!data.length) return null;

        const currentMonthStr = "2026-03"; // Hardcoded for this specific view based on previous logic

        const totalHours = data.reduce((acc, curr) => acc + (curr.fact_hours || 0), 0);
        const monthRows = data.filter(d => d.date.startsWith(currentMonthStr));
        const monthHours = monthRows.reduce((acc, curr) => acc + (curr.fact_hours || 0), 0);

        const annualTarget = 1922;
        const monthlyTarget = 160;

        return {
            totalHours,
            monthHours,
            annualTarget,
            monthlyTarget,
            annualDiff: totalHours - annualTarget,
            monthlyDiff: monthHours - monthlyTarget,
        };
    }, [data]);

    const chartData = useMemo(() => {
        if (!stats) return [];
        return [
            { name: 'Aylıq (Mart)', FAKT: stats.monthHours, HƏDƏF: stats.monthlyTarget },
            { name: 'İllik (2026)', FAKT: stats.totalHours, HƏDƏF: stats.annualTarget }
        ];
    }, [stats]);

    const exportToExcel = () => {
        if (!data || data.length === 0) {
            setError("Eksport etmək üçün məlumat tapılmadı.");
            return;
        }

        // Prepare data for Excel
        const exportData = data.map(row => {
            const d = new Date(row.date);
            const isFact = row.fact_hours > 0;
            const status = isFact ? "İCRA EDİLDİ" : (row.plan === "WORK" ? "İŞ GÜNÜ" : "İSTİRAHƏT");
            const diffInfo = row.diff === "Mismatch" ? "Əlavə İş" : (row.diff === "Missed" ? "Buraxılmış" : "-");

            return {
                "Tarix": row.date,
                "Gün": d.toLocaleDateString("az-AZ", { weekday: "long" }),
                "Cari Durum": status,
                "Plan (Saat)": row.plan === "WORK" ? 8 : 0,
                "Faktiki (Saat)": row.fact_hours || 0,
                "Status": diffInfo,
                "Dövr (GÜN / 30)": row.cycle_day
            };
        });

        // Create workbook and worksheet
        const worksheet = XLSX.utils.json_to_sheet(exportData);
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, "İşçi Cədvəli");

        // Generate Excel file buffer
        const excelBuffer = XLSX.write(workbook, { bookType: "xlsx", type: "array" });

        // Save using file-saver
        const fileData = new Blob([excelBuffer], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8" });
        const employeeName = users.find(u => u.id === contractId)?.name || "Isci";
        saveAs(fileData, `${employeeName}_Cədvəl_${startDate}_${endDate}.xlsx`);
    };

    return (
        <div style={styles.container} className="animate-fade-in">
            <div style={styles.header}>
                <h2 style={styles.title}>
                    <span style={{ fontSize: "28px" }}>📊</span> İşçi Analitikası
                </h2>
                <span style={styles.badge}>Status: Canlı İzləmə</span>
            </div>

            <div style={styles.controls}>
                <div style={styles.formGroup}>
                    <span style={styles.label}>İşçi Axtarışı</span>
                    <SearchableSelect
                        options={users}
                        value={contractId}
                        onChange={(id, name) => {
                            setContractId(id);
                            setSearchQuery(name);
                        }}
                        placeholder="İşçi seçin..."
                    />
                </div>
                <div style={styles.formGroup}>
                    <span style={styles.label}>Başlanğıc</span>
                    <PremiumDatePicker value={startDate} onChange={setStartDate} label="Başlanğıc" />
                </div>
                <div style={styles.formGroup}>
                    <span style={styles.label}>Bitmə</span>
                    <PremiumDatePicker value={endDate} onChange={setEndDate} label="Bitmə" />
                </div>

                <div style={{ display: "flex", gap: "8px", flexWrap: "wrap", marginTop: "10px" }}>
                    <button
                        className="btn"
                        style={{ backgroundColor: "var(--primary-color)", color: "white", borderColor: "var(--primary-color)" }}
                        onClick={handleGenerate}
                        disabled={generating}
                    >
                        {generating ? "⏳ Yüklənir..." : "⚡ Cədvəl Yarat"}
                    </button>
                    <button className="btn" onClick={fetchSchedule}>
                        <span style={{ fontSize: "16px" }}>🔄</span> Yenilə
                    </button>
                    <button
                        className="btn"
                        style={{ backgroundColor: "#15803d", color: "white", borderColor: "#15803d" }}
                        onClick={exportToExcel}
                        title="Məlumatları Excel formatında yükləyin"
                    >
                        <span style={{ fontSize: "16px" }}>📥</span> Excel-ə Çıxar
                    </button>
                </div>
            </div>

            {error && <div style={styles.error} className="animate-slide-down">⚠️ {error}</div>}

            {stats && (stats.monthlyDiff > 0 || stats.annualDiff > 0) && (
                <div style={{
                    padding: "16px", borderRadius: "8px",
                    backgroundColor: "var(--danger-bg)", color: "var(--danger-text)",
                    border: "1px solid var(--danger-border)", marginBottom: "20px",
                    display: "flex", alignItems: "center", gap: "10px", fontWeight: "600"
                }} className="animate-slide-down">
                    <span style={{ fontSize: "20px" }}>🚨</span>
                    BİLDİRİŞ: Seçilmiş işçi {stats.monthlyDiff > 0 ? "Aylıq (160 saat)" : "İllik (1922 saat)"} iş saatı planını  {stats.monthlyDiff > 0 ? stats.monthlyDiff : stats.annualDiff} saat keşmişdir! Mümkün yorğunluq riski var.
                </div>
            )}

            {stats && (
                <div className="animate-slide-down">
                    <div style={styles.stats}>
                        <div style={styles.statCard} className="stat-card-wow">
                            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-end", marginBottom: "8px" }}>
                                <div style={{ ...styles.statValue, color: "var(--success-text)", margin: 0 }}>
                                    {stats.totalHours} saat <span style={{ fontSize: "15px", color: "var(--text-muted)", fontWeight: "500" }}>/ {stats.annualTarget} saat</span>
                                </div>
                                <div style={{ fontSize: "14px", fontWeight: "700", color: "var(--success-text)" }}>
                                    {Math.round((stats.totalHours / stats.annualTarget) * 100)}%
                                </div>
                            </div>
                            <div className="progress-bg">
                                <div className="progress-fill" style={{ width: `${Math.min(100, (stats.totalHours / stats.annualTarget) * 100)}%`, backgroundColor: "var(--success-text)" }}></div>
                            </div>
                            <div style={{ ...styles.statLabel, marginTop: "12px" }}>İllik İş Saatı İzləməsi</div>
                        </div>

                        <div style={styles.statCard} className="stat-card-wow">
                            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-end", marginBottom: "8px" }}>
                                <div style={{ ...styles.statValue, color: stats.monthlyDiff > 0 ? "var(--danger-text)" : "var(--primary-color)", margin: 0 }}>
                                    {stats.monthHours} saat <span style={{ fontSize: "15px", color: "var(--text-muted)", fontWeight: "500" }}>/ {stats.monthlyTarget} saat</span>
                                </div>
                                <div style={{ fontSize: "14px", fontWeight: "700", color: stats.monthlyDiff > 0 ? "var(--danger-text)" : "var(--primary-color)" }}>
                                    {Math.round((stats.monthHours / stats.monthlyTarget) * 100)}%
                                </div>
                            </div>
                            <div className="progress-bg">
                                <div className="progress-fill" style={{ width: `${Math.min(100, (stats.monthHours / stats.monthlyTarget) * 100)}%`, backgroundColor: stats.monthlyDiff > 0 ? "var(--danger-text)" : "var(--primary-color)" }}></div>
                            </div>
                            <div style={{ ...styles.statLabel, marginTop: "12px" }}>Aylıq İş Saatı (Mart)</div>
                        </div>

                        <div style={styles.statCard} className="stat-card-wow">
                            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-end", marginBottom: "8px" }}>
                                <div style={{ ...styles.statValue, color: stats.monthlyDiff > 0 ? "var(--danger-text)" : (stats.monthlyDiff < 0 ? "var(--primary-color)" : "var(--text-muted)"), margin: 0 }}>
                                    {stats.monthlyDiff > 0 ? `+${stats.monthlyDiff} saat (Aşıb)` : `${stats.monthlyDiff} saat (Qalıq)`}
                                </div>
                            </div>
                            <div className="progress-bg" style={{ backgroundColor: "transparent", border: "1px dashed var(--border-color)" }}>
                                <div className="progress-fill" style={{ width: `100%`, backgroundColor: "transparent" }}></div>
                            </div>
                            <div style={{ ...styles.statLabel, marginTop: "12px" }}>160 saat-lıq Limitə Nəzərən Fərq</div>
                        </div>
                    </div>

                    <div style={styles.analyticsRow}>
                        <div style={styles.chartCard} className="wow-chart-card">
                            <h3 style={{ fontSize: "14px", marginBottom: "16px", color: "var(--text-main)", fontWeight: "600", display: "flex", justifyContent: "space-between" }}>
                                Dinamik İş Müqayisəsi (Saat)
                                <span style={{ fontSize: "12px", padding: "2px 8px", backgroundColor: "var(--inactive-btn-bg)", color: "var(--text-muted)", borderRadius: "10px", fontWeight: "700" }}>Statistika</span>
                            </h3>
                            <ResponsiveContainer width="100%" height="85%">
                                <BarChart data={chartData} margin={{ top: 10, right: 10, left: -20, bottom: 0 }} barGap={8}>
                                    <defs>
                                        <linearGradient id="gradFakt" x1="0" y1="0" x2="0" y2="1">
                                            <stop offset="0%" stopColor="var(--success-text)" stopOpacity={1} />
                                            <stop offset="100%" stopColor="var(--success-text)" stopOpacity={0.4} />
                                        </linearGradient>
                                        <linearGradient id="gradHedef" x1="0" y1="0" x2="0" y2="1">
                                            <stop offset="0%" stopColor="var(--primary-color)" stopOpacity={1} />
                                            <stop offset="100%" stopColor="var(--primary-color)" stopOpacity={0.4} />
                                        </linearGradient>
                                    </defs>
                                    <CartesianGrid strokeDasharray="3 3" stroke="var(--border-color)" vertical={false} opacity={0.5} />
                                    <XAxis dataKey="name" stroke="var(--text-muted)" fontSize={12} tickLine={false} axisLine={false} tickMargin={10} />
                                    <YAxis stroke="var(--text-muted)" fontSize={12} tickLine={false} axisLine={false} tickMargin={10} />
                                    <Tooltip
                                        cursor={{ fill: 'var(--inactive-btn-bg)', opacity: 0.4 }}
                                        contentStyle={{ backgroundColor: "var(--bg-card)", borderColor: "var(--border-color)", borderRadius: "12px", boxShadow: "0 10px 25px -5px rgba(0,0,0,0.1)", padding: "12px 16px", border: "1px solid var(--border-color)" }}
                                        itemStyle={{ color: "var(--text-main)", fontWeight: "700", fontSize: "14px", padding: "4px 0" }}
                                    />
                                    <Legend wrapperStyle={{ paddingTop: "15px", fontSize: "13px", color: "var(--text-muted)", fontWeight: "600" }} iconType="circle" />
                                    <Bar dataKey="FAKT" fill="url(#gradFakt)" radius={[6, 6, 0, 0]} barSize={32} background={{ fill: 'var(--inactive-btn-bg)', radius: [6, 6, 0, 0] }} style={{ filter: "drop-shadow(0px 6px 8px rgba(21,128,61,0.25))" }} />
                                    <Bar dataKey="HƏDƏF" fill="url(#gradHedef)" radius={[6, 6, 0, 0]} barSize={32} background={{ fill: 'var(--inactive-btn-bg)', radius: [6, 6, 0, 0] }} style={{ filter: "drop-shadow(0px 6px 8px rgba(37,99,235,0.25))" }} />
                                </BarChart>
                            </ResponsiveContainer>
                        </div>
                        <div style={styles.chartCard} className="wow-chart-card">
                            <h3 style={{ fontSize: "14px", marginBottom: "16px", color: "var(--text-main)", fontWeight: "600", display: "flex", justifyContent: "space-between" }}>
                                İllik Limit İstifadəsi (1922 saat)
                                <span style={{ fontSize: "12px", padding: "2px 8px", backgroundColor: "var(--work-bg)", color: "var(--work-text)", borderRadius: "10px", fontWeight: "700" }}>Canlı</span>
                            </h3>
                            <div style={{ position: "relative", width: "100%", height: "85%", display: "flex", alignItems: "center", justifyContent: "center" }}>
                                <ResponsiveContainer width="100%" height="100%">
                                    <PieChart>
                                        <Pie
                                            data={[
                                                { name: 'İşlənib', value: stats.totalHours },
                                                { name: 'Qalıb', value: Math.max(0, stats.annualTarget - stats.totalHours) }
                                            ]}
                                            cx="50%" cy="50%" innerRadius={80} outerRadius={105} paddingAngle={4} dataKey="value"
                                            stroke="none"
                                            cornerRadius={10}
                                        >
                                            <Cell fill="var(--primary-color)" style={{ filter: "drop-shadow(0px 6px 12px rgba(37,99,235,0.5))" }} />
                                            <Cell fill="var(--border-color)" />
                                        </Pie>
                                        <Tooltip
                                            contentStyle={{ backgroundColor: "var(--bg-card)", borderColor: "var(--border-color)", borderRadius: "12px", boxShadow: "var(--shadow-lg)", padding: "12px", border: "none" }}
                                            itemStyle={{ color: "var(--text-main)", fontWeight: "700", fontSize: "14px" }}
                                            cursor={false}
                                        />
                                        <Legend verticalAlign="bottom" wrapperStyle={{ fontSize: "13px", color: "var(--text-muted)", fontWeight: "600" }} />
                                    </PieChart>
                                </ResponsiveContainer>
                                <div style={{
                                    position: "absolute",
                                    top: "44%",
                                    left: "50%",
                                    transform: "translate(-50%, -50%)",
                                    textAlign: "center",
                                    pointerEvents: "none"
                                }}>
                                    <div style={{ fontSize: "34px", fontWeight: "800", color: "var(--text-main)", lineHeight: "1", letterSpacing: "-1px" }}>
                                        {Math.round((stats.totalHours / stats.annualTarget) * 100)}%
                                    </div>
                                    <div style={{ fontSize: "11px", color: "var(--text-muted)", textTransform: "uppercase", letterSpacing: "1.5px", fontWeight: "600", marginTop: "4px" }}>
                                        İstifadə
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {data.length > 0 && (
                <div style={styles.tableWrapper} className="animate-slide-down">
                    <table style={styles.table}>
                        <thead>
                            <tr>
                                <th style={styles.th}>Tarix</th>
                                <th style={styles.th}>Gün</th>
                                <th style={styles.th}>Cari Durum</th>
                                <th style={styles.th}>Faktiki Saat</th>
                                <th style={styles.th}>Status</th>
                                <th style={styles.th}>Dövr</th>
                            </tr>
                        </thead>
                        <tbody>
                            {data.map((row) => {
                                const d = new Date(row.date);
                                const dayName = d.toLocaleDateString("az-AZ", { weekday: "short" });
                                const isFact = row.fact_hours > 0;

                                return (
                                    <tr key={row.date} className="analytics-row" style={{ backgroundColor: isFact ? "var(--success-bg)" : "transparent" }}>
                                        <td style={{ ...styles.td, fontWeight: "500" }}>{row.date}</td>
                                        <td style={{ ...styles.td, color: "var(--text-muted)" }}>{dayName}</td>
                                        <td style={styles.td}>
                                            {isFact ? (
                                                <span style={styles.statusManagerWork}>Gərgin İş <span style={{ opacity: 0.7, fontSize: "9px" }}>(Fakt)</span></span>
                                            ) : row.plan === "WORK" ? (
                                                <span style={styles.statusWorkPlan}>Plan: WORK <span style={{ opacity: 0.7, fontSize: "9px" }}>(İş)</span></span>
                                            ) : (
                                                <span style={styles.statusRestPlan}>Plan: REST <span style={{ opacity: 0.7, fontSize: "9px" }}>(İstirahət)</span></span>
                                            )}
                                        </td>
                                        <td style={styles.td}>
                                            <span style={{
                                                fontWeight: 700,
                                                fontSize: "14px",
                                                color: isFact ? "var(--success-text)" : "var(--text-muted)"
                                            }}>
                                                {row.fact_hours} saat
                                            </span>
                                        </td>
                                        <td style={styles.td}>
                                            {row.diff ? (
                                                <span style={{
                                                    padding: "4px 8px",
                                                    borderRadius: "4px",
                                                    fontSize: "11px",
                                                    fontWeight: "600",
                                                    backgroundColor: row.diff === "Mismatch" ? "var(--success-bg)" : "var(--danger-bg)",
                                                    color: row.diff === "Mismatch" ? "var(--success-text)" : "var(--danger-text)",
                                                    border: `1px solid ${row.diff === "Mismatch" ? "var(--success-border)" : "var(--danger-border)"}`
                                                }}>
                                                    {row.diff === "Mismatch" ? "Əlavə İş" : "Buraxılmış"}
                                                </span>
                                            ) : (
                                                <span style={{ color: "var(--text-muted)" }}>-</span>
                                            )}
                                        </td>
                                        <td style={{ ...styles.td, color: "var(--text-muted)" }}>{row.cycle_day} <span style={{ opacity: 0.5 }}>/ 30</span></td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            )}

            <style jsx="true">{`
                .analytics-row {
                    transition: background-color 0.2s;
                }
                .analytics-row:hover {
                    background-color: var(--inactive-btn-bg) !important;
                }
                [data-theme='dark'] .analytics-row:hover {
                    background-color: var(--border-color) !important;
                }
                .wow-chart-card {
                    transition: transform 0.3s cubic-bezier(0.4, 0, 0.2, 1), box-shadow 0.3s cubic-bezier(0.4, 0, 0.2, 1);
                }
                .wow-chart-card:hover {
                    transform: translateY(-4px);
                    box-shadow: 0 15px 35px -10px rgba(0,0,0,0.1) !important;
                }
                .stat-card-wow {
                    transition: transform 0.3s ease, box-shadow 0.3s ease;
                }
                .stat-card-wow:hover {
                    transform: translateY(-3px);
                    box-shadow: var(--shadow-md) !important;
                }
                .progress-bg {
                    width: 100%;
                    height: 8px;
                    background-color: var(--inactive-btn-bg);
                    border-radius: 4px;
                    overflow: hidden;
                }
                .progress-fill {
                    height: 100%;
                    border-radius: 4px;
                    transition: width 1s cubic-bezier(0.34, 1.56, 0.64, 1);
                }

                @keyframes fadeInDown {
                    from { opacity: 0; transform: translateY(-10px); }
                    to { opacity: 1; transform: translateY(0); }
                }
                .custom-select-trigger:hover {
                    border-color: var(--primary-color) !important;
                    background-color: var(--inactive-btn-bg) !important;
                }
                .custom-select-trigger.active {
                    border-color: var(--primary-color) !important;
                    box-shadow: 0 0 0 3px rgba(37,99,235,0.1) !important;
                }
                .custom-scrollbar::-webkit-scrollbar {
                    width: 6px;
                }
                .custom-scrollbar::-webkit-scrollbar-track {
                    background: transparent;
                }
                .custom-scrollbar::-webkit-scrollbar-thumb {
                    background: var(--border-color);
                    border-radius: 10px;
                }
                .custom-scrollbar::-webkit-scrollbar-thumb:hover {
                    background: var(--text-muted);
                }
                @keyframes scaleIn {
                    from { transform: scale(0.95); opacity: 0; }
                    to { transform: scale(1); opacity: 1; }
                }
                .animate-scale-in {
                    animation: scaleIn 0.2s cubic-bezier(0.34, 1.56, 0.64, 1);
                }
                .premium-date-input:focus {
                    border-color: var(--primary-color) !important;
                    box-shadow: 0 0 0 3px rgba(37, 99, 235, 0.15) !important;
                    background-color: var(--bg-card) !important;
                }
                .premium-date-input:hover {
                    border-color: var(--primary-color) !important;
                    background-color: var(--bg-card) !important;
                }
                ::-webkit-calendar-picker-indicator {
                    cursor: pointer;
                    opacity: 0;
                    width: 100%;
                    height: 100%;
                    position: absolute;
                    top: 0;
                    left: 0;
                }
                .calendar-day-hover:hover {
                    background-color: var(--inactive-btn-bg) !important;
                    transform: scale(1.1);
                    z-index: 2;
                }
                .btn-icon:hover {
                    background-color: var(--inactive-btn-bg);
                    color: var(--primary-color);
                }
            `}</style>
        </div>
    );
}
